import { addMonths, parseISO } from "date-fns";
import formatDate from "../../utils/formatDate";
import {
  getChargeFailureReason,
  getLastChargeStatusText,
  getOverdueDays,
  getPaymentMethodText,
  getStatusText,
  isAwaitingFirstCharge,
} from "../subscriptions/subscriptionHelpers";

export function statusVariant(subscription) {
  if (subscription.pendingPixPayment || subscription.canRetryCharge || subscription.failedCharge) {
    return { background: "#FFF7ED", border: "#FED7AA", color: "#C2410C" };
  }

  if (subscription.status === "active") {
    return { background: "#F0FDF4", border: "#BBF7D0", color: "#379C5C" };
  }

  if (subscription.status === "canceled") {
    return { background: "#FFF4F4", border: "#FECACA", color: "#C56363" };
  }

  return { background: "#F1F5F9", border: "#E2E8F0", color: "#64748B" };
}

export function subscriptionTitle(subscription) {
  return subscription.courseOffer?.name || `Assinatura #${subscription.id}`;
}

export function subscriptionPrice(subscription) {
  const latestPurchase = [...(subscription.purchases || [])].sort((a, b) => (
    new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
  ))[0];

  return latestPurchase?.productPrice || subscription.price || subscription.courseOffer?.price;
}

export function nextChargeDate(subscription) {
  if (subscription.status !== "active" || !subscription.lastChargeDueAt) return null;

  const parsedLastChargeDate = parseISO(subscription.lastChargeDueAt);
  return isAwaitingFirstCharge(subscription) ? parsedLastChargeDate : addMonths(parsedLastChargeDate, 1);
}

export function paymentMethodLabel(subscription) {
  if (subscription.paymentMethod === "credit_card" && subscription.cardLastDigits) {
    return `${subscription.cardBrand || getPaymentMethodText(subscription.paymentMethod)} ****${subscription.cardLastDigits}`;
  }

  return getPaymentMethodText(subscription.paymentMethod) || "Não informado";
}

export function statusText(subscription) {
  if (subscription.canRetryCharge || subscription.failedCharge) return "Inadimplente";

  return getStatusText(subscription) || subscription.status || "Status indisponível";
}

export function renewalOrEndDateText(subscription, renewalDate) {
  if (renewalDate) return formatDate(renewalDate, "DD/MM/YYYY");
  if (subscription.endDate) return formatDate(subscription.endDate, "DD/MM/YYYY");

  return "-";
}

export function billingDateLabel(subscription) {
  return subscription.status === "canceled" ? "Término" : "Vencimento";
}

export function historyCount(subscription) {
  return subscription.purchases?.length || 0;
}

export function purchaseStatusVariant(purchase) {
  if (purchase.groupStatus === "refunded" || purchase.status === "refunded") {
    return { text: "Reembolsado", background: "#FFF7ED", border: "#FED7AA", color: "#C2410C" };
  }

  if (purchase.groupStatus === "sell" || purchase.status === "sell" || purchase.status === "paid") {
    return { text: "Pago", background: "#F0FDF4", border: "#BBF7D0", color: "#379C5C" };
  }

  if (purchase.status === "canceled" || purchase.status === "failed") {
    return { text: "Negado", background: "#FFF4F4", border: "#FECACA", color: "#C56363" };
  }

  return { text: "Em análise", background: "#FFF7ED", border: "#FED7AA", color: "#EA580C" };
}

export function invoiceLinkFromPurchase(purchase) {
  return purchase.invoiceLink ||
    purchase.invoiceUrl ||
    purchase.invoiceURL ||
    purchase.link ||
    purchase.additionalAttributes?.invoiceLink ||
    purchase.additionalAttributes?.invoiceUrl ||
    purchase.additionalAttributes?.invoiceURL ||
    purchase.additionalAttributes?.notaFiscalUrl ||
    purchase.additionalAttributes?.notaFiscalLink;
}

export function findInvoiceForPurchase(purchase, invoices) {
  const directLink = invoiceLinkFromPurchase(purchase);
  if (directLink) return directLink;

  const matchingInvoice = invoices.find((invoice) => (
    (invoice.transactionCode && invoice.transactionCode === purchase.transactionCode) ||
    (invoice.purchaseId && invoice.purchaseId === purchase.id) ||
    (invoice.productName && invoice.productName === purchase.productName)
  ));

  return matchingInvoice?.link;
}

export function purchasePeriodText(purchase) {
  if (!purchase.createdAt) return null;

  const startDate = formatDate(purchase.createdAt, "DD/MM");
  const endDate = formatDate(addMonths(parseISO(purchase.createdAt), 1), "DD/MM");

  return `${startDate} – ${endDate}`;
}

export function cardBrandName(brand) {
  if (!brand) return "Cartão";

  const normalizedBrand = brand.toLowerCase();
  const brands = {
    visa: "Visa",
    mastercard: "Mastercard",
    elo: "Elo",
    amex: "Amex",
    diners: "Diners",
    discover: "Discover",
    hipercard: "Hipercard",
  };

  return brands[normalizedBrand] || brand;
}

export function cardExpiryText(card) {
  const month = card.expMonth || card.exp_month || card.expirationMonth || card.expiration_month;
  const year = card.expYear || card.exp_year || card.expirationYear || card.expiration_year;

  if (month && year) return `Expira ${String(month).padStart(2, "0")}/${String(year).slice(-4)}`;

  return null;
}

export function overdueDaysText(overdueDays) {
  if (overdueDays === 1) return " — 1 dia";
  if (overdueDays > 1) return ` — ${overdueDays} dias`;

  return "";
}

export function retryDateText(subscription) {
  if (!subscription.lastChargeDueAt) return null;

  return formatDate(addMonths(parseISO(subscription.lastChargeDueAt), 1), "DD/MM/YYYY");
}

export function pendingPixExpiresAt(subscription) {
  return subscription.pendingPixPayment?.additionalAttributes?.maxDaysToKeepWaitingPayment;
}

export function hasPaymentAlert(subscription) {
  return !!subscription.pendingPixPayment || subscription.canRetryCharge || subscription.failedCharge;
}

export function isFutureDate(date) {
  return date ? new Date(date) > new Date() : false;
}

export function formatOptionalDate(date) {
  return date ? formatDate(date, "DD/MM/YYYY") : "-";
}

export { getOverdueDays, getChargeFailureReason, getLastChargeStatusText };
